import React, { useRef, useState } from 'react';
import {
  MenuList as MaterialMenu,
  MenuItem,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Location, NavigateFunction } from 'react-router-dom';
import {
  loggedOut, redirect, redirectToLogin,
} from '../../redux/actions';
import UserId from './UserId';

import { RootState } from '../../redux/types';
import GlobalProcedures from '../../core/globalProcedures';
import ApiClient from '../../loopback/apiClient';
import withRouter, { RouteType } from '../../hoc/withRouter';
import MenuAbsolute from './MenuAbsolute';

type Props = PropsFromRedux & {
  location: Location,
  navigate: NavigateFunction,
};

const Menu: React.FC<Props> = ({
  userToken,
  userId,
  userRoles,
  userRole,
  userCountry,
  router,
  locale,
  loggedOut: loggedOutAction,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const timeoutValue = 300;
  const open = Boolean(anchorEl);
  const timeoutHandler = useRef<NodeJS.Timeout>();
  const anchorElement = useRef<null | HTMLElement>();

  const menuItems = [
    { intlId: 'menu.admin', redirect: '/admin#trials', role: 'admin' },
    { intlId: 'menu.trialRegister', redirect: '/trials' },
    { intlId: 'menu.home', redirect: '/user' },
    { intlId: 'menu.reviews', redirect: '/reviews', role: 'reviewer' },
    { intlId: 'menu.enrollments', redirect: '/enrollments' },
    { intlId: 'menu.certificates', redirect: '/certificates' },
  ];

  const portalUrl = {
    quipPortals: {
      en: 'https://www.qualityinpathology.com/en_GB/biomarker-information',
      de: 'https://www.quip.eu/de_DE/biomarkerinfos',
    },
    pdl1: {
      en: 'https://www.pdl1portal.com',
      de: 'https://www.pdl1portal.eu',
    },
    lugenkarzinom: {
      en: 'https://www.lungenkarzinomportal.eu/en/',
      de: 'https://www.lungenkarzinomportal.eu/de/',
    },
    hrd: {
      en: 'https://www.portal-hrd.eu/en/',
      de: 'https://www.portal-hrd.eu/de/',
    },
  };

  const logoutHandler = async () => {
    const globalProcedures = GlobalProcedures.getInstance();
    const beforeLogout = globalProcedures.procedureMap.get('logout');
    if (beforeLogout) {
      await beforeLogout();
    }
    await ApiClient.logout();
    loggedOutAction();
    redirectToLogin(router.navigate);
  };

  const handleMenuOpenStep2 = () => {
    if (timeoutHandler.current) {
      clearTimeout(timeoutHandler.current);
    }
    if (anchorElement.current) {
      setAnchorEl(anchorElement.current);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (timeoutHandler.current) {
      clearTimeout(timeoutHandler.current);
    }
    anchorElement.current = event.currentTarget;
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    if ((event.relatedTarget as HTMLElement).id !== 'flying-menu-container') {
      if (timeoutHandler.current) {
        clearTimeout(timeoutHandler.current);
      }
      timeoutHandler.current = setTimeout(() => setAnchorEl(null), timeoutValue);
    }
  };

  const handleMenuClose = () => {
    if (timeoutHandler.current) {
      clearTimeout(timeoutHandler.current);
    }

    timeoutHandler.current = setTimeout(() => setAnchorEl(null), timeoutValue);
  };

  const roles = userRoles || (userRole && [userRole]) || [];

  const handlePortalClick = (url: string) => {
    redirect(url);
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        borderRight: '1px solid rgba(0,0,0,0.1)',
        padding: '1em',
        width: '250px',
        minHeight: '100vh',
        position: 'sticky',
        top: 0,
      }}
    >
      <Box
        id="zerpa-sidebar-visible"
        sx={{
          position: 'sticky',
          top: '16px',
        }}
      >
        <MaterialMenu>
          <div style={{ height: '100%' }}>
            <UserId userId={userId} userCountry={userCountry} />
            <Divider />
            <MenuItem
              id="Menu-PDLPortal"
              onClick={() => handlePortalClick(portalUrl.quipPortals[locale])}
              aria-controls={open ? 'portal-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onMouseEnter={handleMenuOpen}
              onMouseLeave={handleMouseLeave}
            >
              <Typography variant="body1">
                <FormattedMessage id="menu.quipBioMarker" />
              </Typography>
            </MenuItem>
            <MenuAbsolute
              id="flying-menu-container"
              open={open}
              anchorEl={anchorEl}
            >
              <MaterialMenu
                id="quip-portal-menu"
                onMouseLeave={handleMenuClose}
                onMouseEnter={handleMenuOpenStep2}
              >
                <MenuItem onClick={() => handlePortalClick(`${portalUrl.pdl1[locale]}/?token=${userToken}&qid=${userId}`)}>
                  <Typography variant="body1">
                    <FormattedMessage id="menu.pdl1Portal" />
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => handlePortalClick(portalUrl.lugenkarzinom[locale])}>
                  <Typography variant="body1">
                    <FormattedMessage id="menu.lungenkarzinomPortal" />
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => handlePortalClick('https://www.mammakarzinomportal.eu/de/')}>
                  <Typography variant="body1">
                    <FormattedMessage id="menu.mammakarzinomPortal" />
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => handlePortalClick('https://www.msi-dmmr-portal.eu/de/')}>
                  <Typography variant="body1">
                    <FormattedMessage id="menu.msiDmmrPortal" />
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => handlePortalClick('https://www.fgfrportal.eu/en/')}>
                  <Typography variant="body1">
                    <FormattedMessage id="menu.fgfrPortal" />
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => handlePortalClick(portalUrl.hrd[locale])}>
                  <Typography variant="body1">
                    <FormattedMessage id="menu.hrdPortal" />
                  </Typography>
                </MenuItem>
              </MaterialMenu>
            </MenuAbsolute>
            <Divider />
            {
              menuItems
                .filter((item) => (
                  item.role ? roles.find((r) => r === item.role) : true
                ))
                .map((item, index) => {
                  const key = `${index}-${item.intlId}`;
                  return (
                    <MenuItem
                      key={key}
                      sx={item.redirect && router.location.pathname.indexOf(item.redirect) > -1 ? { backgroundColor: 'primary.light', color: 'primary.contrastText' } : null}
                      onClick={() => {
                        if (item.redirect) {
                          router.navigate(item.redirect);
                        }
                      }}
                    >
                      <Typography variant="body1">
                        <FormattedMessage id={item.intlId} />
                      </Typography>
                    </MenuItem>
                  );
                })
            }
            <Divider />
            <MenuItem
              id="Menu-logoutButton"
              onClick={logoutHandler}
            >
              <Typography variant="body1">
                <FormattedMessage id="menu.logout" />
              </Typography>
            </MenuItem>
            <Divider />
          </div>
        </MaterialMenu>
      </Box>
    </Box>
  );
};

const mapDispatchToProps = {
  loggedOut,
};

const mapStateToProps = (state: RootState) => ({
  userRoles: state.users.token ? state.users.token.roles : undefined,
  userRole: state.users.token ? state.users.token.role : undefined,
  userId: state.users.token ? state.users.token.userId : undefined,
  userToken: state.users.token ? state.users.token.id : undefined,
  userCountry: state.users.token?.institute?.country || state.users.token?.userCountry,
  locale: state.intl.locale as 'en' | 'de',
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector> & RouteType;
export default injectIntl(connector(withRouter(Menu)));
